import { useEffect, useState } from 'react';
import { collection, query, onSnapshot, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface ProjectDocument {
  id: string;
  documentId: string;
  projectId: string;
  userId: string;
  filename: string;
  fileUrl: string;
  storagePath: string | null;
  contentType: string;
  type: string;
  status: 'processing' | 'completed';
  entityTypes?: string[]; // Which entity types were extracted (e.g., ['URS', 'FS'])
  ursExtracted?: boolean;
  ursCount?: number;
  fsExtracted?: boolean;
  fsCount?: number;
  testExtracted?: boolean;
  testCount?: number;
  riskExtracted?: boolean;
  riskCount?: number;
  sopExtracted?: boolean;
  sopCount?: number;
  embeddingEnabled?: boolean;
  embeddingSucceeded?: boolean;
  chunksCreated?: number;
  embeddingError?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export function useProjectDocuments(projectId: string | undefined) {
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const documentsRef = collection(db, 'projects', projectId, 'documents');
    const q = query(documentsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs: ProjectDocument[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          docs.push({
            id: doc.id,
            documentId: data.documentId || doc.id,
            projectId: data.projectId || projectId,
            userId: data.userId || '',
            filename: data.filename || 'Unknown',
            fileUrl: data.fileUrl || '',
            storagePath: data.storagePath || null,
            contentType: data.contentType || 'application/pdf',
            type: data.type || 'entity_document',
            status: data.status || 'processing',
            entityTypes: data.entityTypes || [],
            ursExtracted: data.ursExtracted || false,
            ursCount: data.ursCount || 0,
            fsExtracted: data.fsExtracted || false,
            fsCount: data.fsCount || 0,
            testExtracted: data.testExtracted || false,
            testCount: data.testCount || 0,
            riskExtracted: data.riskExtracted || false,
            riskCount: data.riskCount || 0,
            sopExtracted: data.sopExtracted || false,
            sopCount: data.sopCount || 0,
            embeddingEnabled: data.embeddingEnabled,
            embeddingSucceeded: data.embeddingSucceeded,
            chunksCreated: data.chunksCreated,
            embeddingError: data.embeddingError ?? null,
            createdAt:
              data.createdAt instanceof Timestamp
                ? data.createdAt.toDate()
                : new Date(),
            updatedAt:
              data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate()
                : new Date(),
          });
        });

        setDocuments(docs);
        setLoading(false);
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching project documents:', error);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  return { documents, loading };
}
